import Box from '@/common/components/Box'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { FileUploader } from '@/common/components/FileUploader'
import GoBackButton from '@/common/components/GoBackButton'
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDateRange } from '@/common/utils/formatters'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useBillingSupplyAuthorization } from '@/features/supplyAuthorizations/hooks/useBillingSupplyAuthorization'
import { useBillingSupplyAuthorizationProductsTotalizer } from '@/features/supplyAuthorizations/hooks/useBillingSupplyAuthorizationProductsTotalizer'
import { ArrowUpRightIcon, BriefcaseBusinessIcon, ChevronsRight } from 'lucide-react'
import { useParams } from 'react-router-dom'

const SupplyAuthorizationsShow = () => {
  const { id } = useParams()

  const {
    data: billingSupplyAuthorization,
    error: billingSupplyAuthorizationError = {} as any,
    refetch: refetchBillingSupplyAuthorization,
  } = useBillingSupplyAuthorization({ billId: id || '' })

  const { data: billingSupplyAuthorizationProductsTotalizer } = useBillingSupplyAuthorizationProductsTotalizer({
    billId: id || '',
  })

  return (
    <div className="text-white-800">
      <div className="flex items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton dataTestid="go_back" fallback="/supply-authorizations" searchLastRouteFromFallback />

          <ImageWithDisplayName
            placeholderIcon={BriefcaseBusinessIcon}
            imageUrl={billingSupplyAuthorization?.bill?.client_branch?.company?.general_information?.company_logo?.url}
            displayName={billingSupplyAuthorization?.bill?.client_branch?.company?.general_information?.fantasy_name}
            displayNameClassName="text-sm font-bold text-white-400"
          />

          <ChevronsRight color="#ffffffb3" />

          {billingSupplyAuthorization?.government_allocation?.branch?.general_information && (
            <>
              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={
                  billingSupplyAuthorization?.government_allocation?.branch?.general_information?.company_logo?.url
                }
                displayName={
                  billingSupplyAuthorization?.government_allocation?.branch?.general_information?.fantasy_name
                }
                displayNameClassName="text-sm font-bold text-white-400"
              />

              <ChevronsRight color="#ffffffb3" />
            </>
          )}

          <p>{billingSupplyAuthorization?.allocation_accounting_number}</p>
        </div>
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-8rem)]"
        statusCode={billingSupplyAuthorizationError?.status}
        refetchFunction={refetchBillingSupplyAuthorization}
      >
        <Box className="max-h-[calc(100vh-8rem)] flex flex-col gap-6 rounded-none overflow-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <p className="text-lg font-bold">Autorização de fornecimento</p>

          <Box className="flex flex-col gap-4 bg-white-200 self-start min-w-[25rem] p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-light italic">N do Empenho</p>
                <p className="text-sm font-bold">
                  {billingSupplyAuthorization?.government_allocation?.main_entry?.office_number}
                </p>
              </div>

              <ArrowUpRightIcon className="size-6" strokeWidth={1} />
            </div>

            <div>
              <p className="text-[10px] font-light italic">Filial</p>

              <ImageWithDisplayName
                placeholderIcon={BriefcaseBusinessIcon}
                imageUrl={
                  billingSupplyAuthorization?.government_allocation?.branch?.general_information
                    ? billingSupplyAuthorization?.government_allocation?.branch?.general_information?.company_logo?.url
                    : billingSupplyAuthorization?.government_allocation?.branch?.company?.general_information
                      ?.company_logo?.url
                }
                displayName={
                  billingSupplyAuthorization?.government_allocation?.branch?.general_information
                    ? billingSupplyAuthorization?.government_allocation?.branch?.general_information?.fantasy_name
                    : billingSupplyAuthorization?.government_allocation?.branch?.company?.general_information
                      ?.fantasy_name
                }
                displayNameClassName="text-sm font-bold"
              />
            </div>

            <div className="flex items-start gap-4">
              <div>
                <p className="text-[10px] font-light italic">Valor aprovado</p>

                <p className="text-sm font-bold">
                  {formatCurrency(Number(billingSupplyAuthorization?.government_allocation?.approved_value || 0))}
                </p>
              </div>

              <div>
                <p className="text-[10px] font-light italic">Período de competência</p>

                {billingSupplyAuthorization?.government_allocation ? (
                  <p className="text-sm font-bold">
                    {`${dateTransformer(
                      billingSupplyAuthorization?.government_allocation?.applicable_start_date || '',
                    )} à ${dateTransformer(
                      billingSupplyAuthorization?.government_allocation?.applicable_end_date || '',
                    )}`}
                  </p>
                ) : (
                  <p className="text-sm font-bold">
                    --
                  </p>
                )}
              </div>
            </div>
          </Box>

          <div className="flex items-start gap-4">
            <div>
              <p className="text-sm font-light italic">N do Empenho contábil</p>

              <p className="text-lg font-bold">{billingSupplyAuthorization?.allocation_accounting_number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Fatura</p>

              <p className="text-lg font-bold">{billingSupplyAuthorization?.bill.number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Período da Fatura</p>

              <p className="text-lg font-bold">
                {billingSupplyAuthorization?.bill?.opened_at && billingSupplyAuthorization?.bill?.closed_at
                  ? formatDateRange(
                    new Date(billingSupplyAuthorization?.bill?.opened_at),
                    new Date(billingSupplyAuthorization?.bill?.closed_at),
                  )
                  : '--'}
              </p>
            </div>

            <div className="text-yellow-400">
              <p className="text-sm font-light italic">Valor total consumido</p>

              <p className="text-lg font-bold">
                {formatCurrency(Number(billingSupplyAuthorization?.bill.consumed_total || 0))}
              </p>
            </div>
          </div>

          <div className="w-full h-[3px] bg-white-200" />

          <FileUploader
            className="min-w-44"
            fileName={
              billingSupplyAuthorization?.file?.url
                ? getFilenameFromUrl(billingSupplyAuthorization?.file?.url)
                : undefined
            }
            currentFileUrl={billingSupplyAuthorization?.file?.url ? billingSupplyAuthorization?.file?.url : undefined}
            disabled
          />

          <Box className="flex flex-col gap-4 p-4">
            {billingSupplyAuthorizationProductsTotalizer?.map((productTotalizer) => {
              return (
                <div key={productTotalizer.category_name} className="flex flex-col gap-4">
                  <p className="text-sm">{productTotalizer.category_name}</p>

                  <div className="w-full h-px bg-white-100" />

                  {productTotalizer.products.map((product) => {
                    return (
                      <div key={product.product_name} className="flex items-stretch gap-4">
                        <div className="w-px bg-blue-800" />

                        <div className="flex items-center justify-between w-full">
                          <p className="text-sm">{product.product_name}</p>

                          <div className="text-yellow-400">
                            <p className="text-sm font-light italic">Valor total consumido</p>

                            <p className="text-lg font-bold">{formatCurrency(Number(product.total))}</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </Box>
        </Box>
      </ErrorMessageWrapper>
    </div>
  )
}

export default SupplyAuthorizationsShow
